import React, { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { RotateCcw, CirclePlus, Send, Trash2, LoaderCircle } from "lucide-react";
import styles from "./HeaderBar.module.css";

import { Timer } from "./Timer";
import { MatterSelector, type Matter } from "./MatterSelector";
import { DateRangePicker } from "./DateRangePicker";
import ConfirmableAction from "./ConfirmableAction";
import ApplicationFilter from "./ApplicationFilter";
import ClientMatterFilter, { type ClientMatterFilterMatter } from "./ClientMatterFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { type FilterableAppId } from "../../hooks/useApplicationFilter";

// Action icons
import combineIcon from "@/assets/applications/CombineIcon.svg";
import combineIconWhite from "@/assets/applications/CombineIconWhite.svg";
import duplicateIcon from "@/assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "@/assets/applications/DuplicateIconWhite.svg";

type ViewMode = "entries" | "history" | "deleted" | "calendar" | "settings";

export interface HeaderBarProps {
  viewMode: ViewMode;
  date: Date;
  onDateChange: (d: Date) => void;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  onDateRangeChange?: (r: { from: Date | undefined; to: Date | undefined }) => void;
  leftContent?: React.ReactNode;
  timerRunning: boolean;
  elapsedSeconds: number;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  selectionCount: number;
  onAddEntry: () => void;
  onCombine: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUndo?: () => void;
  isUndoing?: boolean;
  onExport: () => void;
  isExporting?: boolean;
  actionInProgress?: "combine" | "delete" | "duplicate" | "restore" | null;
  matters?: Matter[];
  hardcodedMatter?: string;
  onHardcodedMatterChange?: (matterId: string) => void;
  // Application filter props
  isApplicationVisible?: (appId: FilterableAppId) => boolean;
  onToggleApplication?: (appId: FilterableAppId) => void;
  isFilterActive?: boolean;
  onShowAllApplications?: () => void;
  onHideAllApplications?: () => void;
  // Client/matter filter props
  filterableClients?: string[];
  filterableMatters?: ClientMatterFilterMatter[];
  clientFilter?: string | null;
  matterFilter?: string | null;
  onClientFilterChange?: (client: string | null) => void;
  onMatterFilterChange?: (matterId: string | null, client: string | null) => void;
  // Team page overrides
  showTimer?: boolean;
  rightContent?: React.ReactNode;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  viewMode,
  date,
  onDateChange,
  dateRange,
  onDateRangeChange,
  leftContent,
  timerRunning,
  elapsedSeconds,
  onStart,
  onStop,
  onReset,
  selectionCount,
  onAddEntry,
  onCombine,
  onDuplicate,
  onDelete,
  onUndo,
  isUndoing = false,
  onExport,
  isExporting = false,
  actionInProgress = null,
  matters = [],
  hardcodedMatter = "",
  onHardcodedMatterChange,
  isApplicationVisible,
  onToggleApplication,
  isFilterActive = false,
  onShowAllApplications,
  onHideAllApplications,
  filterableClients,
  filterableMatters,
  clientFilter,
  matterFilter,
  onClientFilterChange,
  onMatterFilterChange,
  showTimer = true,
  rightContent,
}) => {
  const { darkMode } = useDarkMode();
  const anyBusy = isExporting || actionInProgress !== null;

  // Defer loading spinners by 200ms so fast operations don't flash
  const [deferredAction, setDeferredAction] = useState(actionInProgress);
  const [deferredExporting, setDeferredExporting] = useState(isExporting);
  useEffect(() => {
    if (actionInProgress) {
      const id = setTimeout(() => setDeferredAction(actionInProgress), 200);
      return () => clearTimeout(id);
    }
    setDeferredAction(null);
  }, [actionInProgress]);
  useEffect(() => {
    if (isExporting) {
      const id = setTimeout(() => setDeferredExporting(true), 200);
      return () => clearTimeout(id);
    }
    setDeferredExporting(false);
  }, [isExporting]);
  const isEntries = viewMode === "entries";
  const isHistory = viewMode === "history";
  const isDeleted = viewMode === "deleted";
  const isCalendar = viewMode === "calendar";

  // Date navigation handlers
  const goToPreviousDay = () => {
    const prev = new Date(date);
    prev.setDate(prev.getDate() - 1);
    onDateChange(prev);
  };

  const goToNextDay = () => {
    const next = new Date(date);
    next.setDate(next.getDate() + 1);
    onDateChange(next);
  };

  const hasTopRow = !!leftContent || (showTimer && (isEntries || isHistory || isDeleted)) || (!showTimer && !!rightContent);

  return (
    <div className={styles.headerWrapper} style={!hasTopRow ? { minHeight: "unset", paddingTop: 16, paddingBottom: 12 } : undefined}>
      {/* ============================================
          TOP ROW: SummaryCards | Timer + MatterSelector
          ============================================ */}
      {hasTopRow && (
      <div className={styles.topRow}>
        {/* Left: Summary Cards (40%) */}
        <div className={styles.topLeft}>
          {leftContent}
        </div>

        {/* Right: Timer + MatterSelector stacked (shown for entries and history) */}
        {(isEntries || isHistory || isDeleted) && showTimer && (
          <div className={styles.topRight}>
            {/* Timer - 180px, left-aligned */}
            {!isCalendar && (
              <div className={styles.timerControl}>
                <Timer
                  elapsedSeconds={elapsedSeconds}
                  isRunning={timerRunning}
                  onStart={onStart}
                  onStop={onStop}
                  onReset={onReset}
                />
              </div>
            )}

            {/* Hardcoded Matter Selector - 180px */}
            {onHardcodedMatterChange && (
              <div className={styles.matterControl}>
                <MatterSelector
                  matters={matters}
                  value={hardcodedMatter}
                  onChange={onHardcodedMatterChange}
                />
              </div>
            )}
          </div>
        )}
        {/* Custom right content (e.g., tab pills for team page) */}
        {!showTimer && rightContent && (
          <div className={styles.topRight} style={{ justifyContent: "center" }}>
            {rightContent}
          </div>
        )}
      </div>
      )}

      {/* ============================================
          BOTTOM ROW: ActionButtons+Matter | Timer+Add+Export
          ============================================ */}
      {isEntries && (
        <div className={styles.bottomRow}>
          {/* Left: Action Buttons (70%) */}
          <div className={styles.bottomLeft}>
            <TooltipProvider delayDuration={300}>
              <div className={styles.actionButtons}>
                {/* Filter group - ApplicationFilter + ClientMatterFilter, 16px gap to action icons */}
                <div className={styles.filterGroup}>
                  {isApplicationVisible && onToggleApplication && (
                    <ApplicationFilter
                      isVisible={isApplicationVisible}
                      onToggle={onToggleApplication}
                      isFilterActive={isFilterActive}
                      onShowAll={onShowAllApplications || (() => {})}
                      onHideAll={onHideAllApplications || (() => {})}
                    />
                  )}
                  <ClientMatterFilter
                    clients={filterableClients}
                    matters={filterableMatters}
                    clientFilter={clientFilter}
                    matterFilter={matterFilter}
                    onClientChange={onClientFilterChange}
                    onMatterChange={onMatterFilterChange}
                  />
                </div>

                {/* Edit, Combine, Duplicate, Delete - action icons */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={selectionCount === 0 || anyBusy}
                        loading={deferredAction === "combine"}
                        className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
                        onClick={onCombine}
                      >
                        <img
                          src={darkMode ? combineIconWhite : combineIcon}
                          alt="Combine"
                          className="h-4 w-4"
                        />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Combine</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={selectionCount === 0 || anyBusy}
                        loading={deferredAction === "duplicate"}
                        className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
                        onClick={onDuplicate}
                      >
                        <img
                          src={darkMode ? duplicateIconWhite : duplicateIcon}
                          alt="Duplicate"
                          className="h-4 w-4"
                        />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Duplicate</p>
                  </TooltipContent>
                </Tooltip>

                {/* Delete - destructive action with confirmation */}
                <ConfirmableAction
                  icon={<Trash2 className="h-4 w-4" />}
                  confirmText={() => `Delete ${selectionCount} ${selectionCount === 1 ? "entry" : "entries"}?`}
                  onConfirm={onDelete}
                  iconClassName={`${styles.actionButtonIcon} ${styles.deleteButton} disabled:opacity-40 disabled:cursor-not-allowed`}
                  confirmClassName={`${styles.actionButtonWide} ${styles.deleteConfirmButton}`}
                  tooltip="Delete"
                  resetTrigger={selectionCount}
                  disabled={selectionCount === 0 || anyBusy}
                  loading={deferredAction === "delete"}
                />

                {/* Date Range Picker - compact with navigation */}
                <div className={styles.datePickerInline}>
                  <DateRangePicker
                    date={date}
                    onDateChange={onDateChange}
                    dateRange={dateRange}
                    onDateRangeChange={onDateRangeChange}
                    showPresets={isEntries}
                    compact
                    onPrevious={goToPreviousDay}
                    onNext={goToNextDay}
                  />
                </div>
              </div>
            </TooltipProvider>
          </div>

					{/* Right: Add + Export (30%) */}
					<div className={styles.bottomRight}>
						<div className={styles.primaryActions}>
							<Button
								variant="default"
								className="h-12 bg-[var(--brand-dark-teal)] text-white hover:bg-[var(--brand-dark-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center"
								onClick={onAddEntry}
							>
								<CirclePlus className="h-[18px] w-[18px]" strokeWidth={1.5} />
								Add
							</Button>
							<Button
								variant="default"
								onClick={onExport}
								disabled={anyBusy}
								className="h-12 bg-[var(--brand-teal)] text-white hover:bg-[var(--brand-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
							>
								{deferredExporting ? <LoaderCircle className="h-[18px] w-[18px] animate-spin" /> : <Send className="h-[18px] w-[18px]" />}
								{deferredExporting ? "Exporting..." : "Export"}
							</Button>
						</div>
					</div>
				</div>
			)}

      {/* History view: Show Filter + DatePicker (no action buttons, no Add/Export) */}
      {isHistory && (
        <div className={styles.bottomRow}>
          <div className={styles.bottomLeft}>
            <TooltipProvider delayDuration={300}>
              <div className={styles.actionButtons}>
                {/* Filter group - ApplicationFilter + ClientMatterFilter, 16px gap to next control */}
                <div className={styles.filterGroup}>
                  {isApplicationVisible && onToggleApplication && (
                    <ApplicationFilter
                      isVisible={isApplicationVisible}
                      onToggle={onToggleApplication}
                      isFilterActive={isFilterActive}
                      onShowAll={onShowAllApplications || (() => {})}
                      onHideAll={onHideAllApplications || (() => {})}
                    />
                  )}
                  <ClientMatterFilter
                    clients={filterableClients}
                    matters={filterableMatters}
                    clientFilter={clientFilter}
                    matterFilter={matterFilter}
                    onClientChange={onClientFilterChange}
                    onMatterChange={onMatterFilterChange}
                  />
                </div>

                {/* Date Range Picker - no wrapper margin since it's directly after Filter */}
                <DateRangePicker
                  date={date}
                  onDateChange={onDateChange}
                  dateRange={dateRange}
                  onDateRangeChange={onDateRangeChange}
                  showPresets
                  compact
                  onPrevious={goToPreviousDay}
                  onNext={goToNextDay}
                />
              </div>
            </TooltipProvider>
          </div>
          <div className={styles.bottomRight} />
        </div>
      )}

      {/* Deleted view: Filter + DatePicker on left, Undo button on right */}
      {isDeleted && (
        <div className={styles.bottomRow}>
          <div className={styles.bottomLeft}>
            <TooltipProvider delayDuration={300}>
              <div className={styles.actionButtons}>
                <div className={styles.filterGroup}>
                  {isApplicationVisible && onToggleApplication && (
                    <ApplicationFilter
                      isVisible={isApplicationVisible}
                      onToggle={onToggleApplication}
                      isFilterActive={isFilterActive}
                      onShowAll={onShowAllApplications || (() => {})}
                      onHideAll={onHideAllApplications || (() => {})}
                    />
                  )}
                  <ClientMatterFilter
                    clients={filterableClients}
                    matters={filterableMatters}
                    clientFilter={clientFilter}
                    matterFilter={matterFilter}
                    onClientChange={onClientFilterChange}
                    onMatterChange={onMatterFilterChange}
                  />
                </div>
                <DateRangePicker
                  date={date}
                  onDateChange={onDateChange}
                  dateRange={dateRange}
                  onDateRangeChange={onDateRangeChange}
                  showPresets
                  compact
                  onPrevious={goToPreviousDay}
                  onNext={goToNextDay}
                />
              </div>
            </TooltipProvider>
          </div>
          <div className={styles.bottomRight}>
            <div className={styles.primaryActions}>
              <Button
                variant="default"
                onClick={onUndo}
                disabled={selectionCount === 0 || anyBusy}
                className="h-12 bg-[var(--brand-teal)] text-white hover:bg-[var(--brand-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isUndoing ? <LoaderCircle className="h-[18px] w-[18px] animate-spin" /> : <RotateCcw className="h-[18px] w-[18px]" />}
                {isUndoing ? "Restoring..." : "Restore"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderBar;
